import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import CameraModelSettings from './CameraModelSettings';
import { CameraForm, initialState, toCamera, toForm } from './CameraForm';
import Login from '../Login';
import { models } from './CameraModels';
import Emails from './Emails';
import AllowedDomains from './AllowedDomains';
import FastPlaybacksComponent from './FastPlaybacks';
import FrameCapturesComponent from './FrameCaptures';
import LocationComponent from './Location';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  MenuItem,
  Paper,
} from '@mui/material';
import { GQL_UPDATE_CAMERA } from '../../graphql/mutations/cameras';
import { useMutation, useQuery } from '@apollo/client';
import { GQL_GET_CAMERA } from '../../graphql/queries/cameras';
import UTextField2 from '../../utils/UTextField2';
import UCheckbox from '../../utils/UCheckbox';
import USelect from '../../utils/USelect';
import RecordComponent from './Record';

export default function CameraSettings() {
  const { id } = useParams<{ id: string }>();

  const methods = useForm<CameraForm>({
    defaultValues: { ...initialState },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    shouldFocusError: true,
  });
  const { handleSubmit, reset } = methods;
  const history = useHistory();
  const { authorized, removeToken } = useContext(AuthenticationContext);

  const isNew = id == 'new';
  const [loading, setLoading] = useState(!isNew);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [clientName, setClientName] = useState();
  const variables = { cameraId: id };
  const query = GQL_GET_CAMERA;
  const { data } = useQuery(query, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        setError(`Failed to load data ${error.message}`);
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted(data) {
      console.log(data);
      setLoading(false);

      setClientName(data.camera.client.name);

      const form = toForm(data.camera);
      reset(form);
    },
    variables,
    skip: !authorized,
  });
  const [updateCamera] = useMutation(GQL_UPDATE_CAMERA, {
    refetchQueries: [{ query: GQL_GET_CAMERA, variables: { cameraId: id } }],
    awaitRefetchQueries: true,
  });

  if (!authorized) return <Login />;

  const onSubmit = (form: CameraForm) => {
    const variables: any = {
      input: toCamera(form),
    };

    variables.input.id = id;
    if (!variables.input.password) delete variables.input.password;
    updateCamera({ variables })
      .then(() => {
        setLoading(false);
        setError('');
        setSuccess('Account updated!');
        history.push(`/camera/${id}`);
      })
      .catch((err) => {
        setLoading(false);
        setError(`Failed to update account ${err.message}`);
        console.error(err);
      });
  };

  return (
    <Card>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid container spacing={2} sx={{ margin: '0 0 20px 0' }} alignItems="center">
                <Grid item xs={12} sm>
                  <h4 style={{ margin: 0, cursor: 'pointer' }}>{clientName || ''}</h4>
                  <h1 style={{ margin: 0 }}>Camera Settings</h1>
                </Grid>
                <Grid item xs="auto" sm="auto"></Grid>
                <Grid item flexGrow={{ xs: 1, sm: 0 }} flexShrink={{ sm: 1 }}></Grid>
                <Grid item xs="auto" sm="auto">
                  <Button type="submit" color="secondary" disabled={loading} variant="contained">
                    Update
                  </Button>
                </Grid>
              </Grid>

              {loading && (
                <Grid item container xs={12} minHeight="300px" justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Grid>
              )}
              {success && <h3>{success}</h3>}
              {error && <h3>{error}</h3>}
              {!loading && (
                <Grid item sm={6} xs={12} container spacing={2} alignItems="flex-start" alignContent="flex-start">
                  <Grid item xs={12}>
                    <UTextField2
                      name="name"
                      options={{
                        required: 'Name is required',
                        minLength: { value: 3, message: 'Name must have at least 3 characters' },
                        maxLength: { value: 50, message: 'Name must have at max 50 characters' },
                      }}
                      fullWidth
                      label="Name"
                      shrink={true}
                      defaultValue=""
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <UTextField2
                      name="controlTime"
                      options={{
                        required: 'Control time is required',
                        min: { value: 0, message: 'Negative not allowed' },
                        max: { value: 3600, message: 'Should be lower than 3600' },
                        valueAsNumber: true,
                      }}
                      endAdornment={<InputAdornment position="end">seconds</InputAdornment>}
                      fullWidth
                      shrink={true}
                      label="Control Time"
                      defaultValue="0"
                      required
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {!!data?.servers && (
                      <USelect name="server" fullWidth label="Server">
                        {data?.servers.map((server: any) => {
                          return (
                            <MenuItem value={server.id} key={server.id}>
                              {server.subdomain}
                            </MenuItem>
                          );
                        })}
                      </USelect>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <UCheckbox name="active" label="Active" color="secondary" defaultValue={true} />
                  </Grid>
                  <Grid item xs={6}>
                    <UCheckbox name="keepConnected" label="Keep Connected" color="secondary" defaultValue={true} />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper style={{ backgroundColor: '#eee', padding: '16px 10px' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <USelect
                            name="model"
                            label="Model"
                            fullWidth
                            rules={{
                              required: 'Model is required',
                            }}
                          >
                            {Object.entries(models).map(([model, name]: [model: string, name: string]) => {
                              return (
                                <MenuItem value={model} key={model}>
                                  {name}
                                </MenuItem>
                              );
                            })}
                          </USelect>
                        </Grid>
                        <CameraModelSettings />
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper style={{ backgroundColor: '#fff7bb', padding: '16px 10px' }}>
                      <Grid container spacing={2}>
                        <Grid item flexGrow={1}>
                          <h1 style={{ fontSize: '14px', margin: 0 }}>Monitoring</h1>
                          <UCheckbox name="alert.enabled" label="Enabled" color="secondary" defaultValue={false} />
                        </Grid>
                        <Grid item>
                          <UTextField2
                            name="alert.interval"
                            options={{
                              required: 'Interval is required',
                              min: { value: 1, message: 'Should be greater or equal to one' },
                              max: { value: 60, message: 'Should be lower than 60' },
                              valueAsNumber: true,
                            }}
                            shrink={true}
                            endAdornment={<InputAdornment position="end">minutes</InputAdornment>}
                            fullWidth
                            label="Interval"
                            defaultValue="5"
                            required
                            type="number"
                          />
                        </Grid>
                        <Emails />
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper style={{ backgroundColor: '#dfd', padding: '16px 10px' }}>
                      <Grid container spacing={2}>
                        <AllowedDomains />
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    {!isNew && (
                      <Paper style={{ backgroundColor: '#d0e6ff', padding: '16px 10px' }}>
                        <Grid container>
                          <Grid item flexGrow={1}>
                            <h1 style={{ fontSize: '14px', margin: 0 }}>Panoramas</h1>
                          </Grid>
                          <Grid item></Grid>
                          <Grid item xs={12}>
                            <List>
                              {data?.camera?.panoramas.map((panorama: any) => (
                                <ListItem key={panorama.id}>
                                  <ListItemIcon>
                                    <PhotoSizeSelectLargeIcon />
                                  </ListItemIcon>
                                  {panorama.ref}
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        </Grid>
                      </Paper>
                    )}
                  </Grid>
                </Grid>
              )}
              <Grid item sm={6} xs={12} container spacing={2} alignItems="flex-start" alignContent="flex-start">
                <LocationComponent display={!loading} />
                {!loading && (
                  <>
                    <RecordComponent />
                    <FastPlaybacksComponent />
                    <FrameCapturesComponent />
                  </>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </form>
      </FormProvider>
    </Card>
  );
}
